'use client'

import { useEffect, useState } from 'react'
import { usePathname } from 'next/navigation'
import { Menu, X } from 'lucide-react'
import { GOLD, TEXT, TEXT_SECONDARY, BORDER, BORDER_GOLD, LOGO_WHITE } from '@/app/lib/portal/theme'
import { DIVISION_LINE } from '../lib/content'
import { NAV_LINKS, PATHS, SITE } from '../lib/site'
import { SiteLink, cleanPath } from './SiteBase'

/**
 * The site header: the canonical logo (never redrawn — Mark, 2026-08-01), the
 * page links, and a quiet provider log-in. Transparent over the hero, then a
 * frosted navy band with a gold hairline once the page scrolls (the portal's
 * sticky-nav treatment). On phones the links open as a panel under the bar.
 *
 * Above it, on every page, a slim strip names the Division (Mark, 2026-09-26).
 * It is text, not the tagline logo: at the header's 172px the lockup's tagline
 * would render about 1px tall (lint:logo-tagline wants 480px or more). The
 * strip scrolls away; the bar under it stays sticky.
 */
export function SiteHeader() {
  const current = cleanPath(usePathname())
  const [scrolled, setScrolled] = useState(false)
  const [open, setOpen] = useState(false)

  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 12)
    on()
    window.addEventListener('scroll', on, { passive: true })
    return () => window.removeEventListener('scroll', on)
  }, [])

  // Close the phone menu on navigation.
  useEffect(() => setOpen(false), [current])

  const solid = scrolled || open

  return (
    <>
      <div className="relative z-[1]" style={{ background: 'rgba(9,21,37,0.6)', borderBottom: `1px solid ${BORDER}` }}>
        <p
          className="m-0 mx-auto px-5 py-2 text-center text-[9.5px] font-bold uppercase tracking-[0.12em] sm:px-8 sm:text-[10.5px] sm:tracking-[0.22em]"
          style={{ color: TEXT_SECONDARY, maxWidth: 1180 }}
        >
          {DIVISION_LINE}
        </p>
      </div>
      <header
        className="ms-header sticky top-0 z-50"
        style={{
          background: solid ? 'rgba(9,21,37,0.88)' : 'transparent',
          borderBottom: `1px solid ${solid ? BORDER_GOLD : 'transparent'}`,
          backdropFilter: solid ? 'blur(12px)' : undefined,
          WebkitBackdropFilter: solid ? 'blur(12px)' : undefined,
        }}
      >
        <div className="relative mx-auto flex items-center justify-between gap-6 px-5 sm:px-8" style={{ maxWidth: 1180, height: 76 }}>
          <SiteLink href={PATHS.home} className="flex-none" aria-label="Meridian Anesthesia — home">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src={LOGO_WHITE} alt="" aria-hidden="true" style={{ width: 172, height: 'auto', display: 'block' }} />
          </SiteLink>

          <nav aria-label="Main" className="hidden lg:block">
            <ul className="m-0 flex list-none items-center gap-1 p-0">
              {NAV_LINKS.map((l) => {
                const active = current === l.href
                return (
                  <li key={l.href}>
                    <SiteLink
                      href={l.href}
                      aria-current={active ? 'page' : undefined}
                      className="ms-navlink rounded-lg px-3.5 py-2 text-[14.5px] font-semibold no-underline"
                      style={{ color: active ? GOLD : TEXT_SECONDARY }}
                    >
                      {l.label}
                    </SiteLink>
                  </li>
                )
              })}
            </ul>
          </nav>

          <div className="hidden items-center gap-3 lg:flex">
            <a
              href={SITE.loginUrl}
              className="ms-navlink rounded-lg px-3 py-2 text-[13px] font-semibold no-underline"
              style={{ color: TEXT_SECONDARY }}
            >
              Provider log in
            </a>
            <SiteLink
              href={PATHS.careers}
              className="mp-btn inline-flex items-center rounded-xl px-5 text-[14px] font-semibold no-underline"
              style={{ background: GOLD, color: 'var(--eq-navy)', paddingTop: 10, paddingBottom: 10 }}
            >
              Join Meridian
            </SiteLink>
          </div>

          <button
            type="button"
            className="grid place-content-center rounded-xl lg:hidden"
            style={{ width: 44, height: 44, border: `1px solid ${BORDER_GOLD}`, color: GOLD, background: 'transparent' }}
            aria-expanded={open}
            aria-controls="ms-mobile-nav"
            aria-label={open ? 'Close menu' : 'Open menu'}
            onClick={() => setOpen((o) => !o)}
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>

          {open && (
            <div
              id="ms-mobile-nav"
              className="absolute left-0 right-0 top-full px-5 pb-6 pt-2 lg:hidden"
              style={{ background: 'rgba(9,21,37,0.97)', borderBottom: `1px solid ${BORDER_GOLD}` }}
            >
              <ul className="m-0 list-none p-0">
                {[{ label: 'Home', href: PATHS.home }, ...NAV_LINKS].map((l) => {
                  const active = current === l.href
                  return (
                    <li key={l.href} style={{ borderBottom: `1px solid ${BORDER}` }}>
                      <SiteLink
                        href={l.href}
                        aria-current={active ? 'page' : undefined}
                        className="block py-3.5 text-[17px] no-underline"
                        style={{ color: active ? GOLD : TEXT, fontFamily: 'Georgia, serif' }}
                      >
                        {l.label}
                      </SiteLink>
                    </li>
                  )
                })}
              </ul>
              <div className="mt-5 flex flex-col gap-3">
                <SiteLink
                  href={PATHS.careers}
                  className="mp-btn inline-flex items-center justify-center rounded-xl text-[15px] font-semibold no-underline"
                  style={{ background: GOLD, color: 'var(--eq-navy)', paddingTop: 12, paddingBottom: 12 }}
                >
                  Join Meridian
                </SiteLink>
                <a
                  href={SITE.loginUrl}
                  className="inline-flex items-center justify-center rounded-xl text-[14px] font-semibold no-underline"
                  style={{ color: GOLD, border: `1px solid ${BORDER_GOLD}`, paddingTop: 11, paddingBottom: 11 }}
                >
                  Provider log in
                </a>
              </div>
            </div>
          )}
        </div>
      </header>
    </>
  )
}
